import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '@emotion/react';
import {
  Box,
  Collapse,
  Grow,
  IconButton,
  Slide,
  Typography,
  CircularProgress,
  Tooltip,
  Button,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ThumbUp as ShortlistedIcon,
  ThumbDown as RejectedIcon,
  ErrorOutlineRounded as FailedIcon,
  FileCopyOutlined as DuplicateIcon,
  TaskAltOutlined as SuccessIcon,
  NotificationsOutlined as NotifyIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import { useProcessingSnackbar } from 'context/ProcessingSnackbarContext';
import { getBatchConfig } from 'config/batchConfig';
import { FetchBatchStatus } from 'api/bulk-analysis';
import { FetchDealflowShortlistBatchStatus } from 'api/startup_applications';
import { useNavigate } from 'react-router-dom';

// const FILE_STATUS_META = {
//   created: { Icon: SuccessIcon, color: '#16A34A', tooltip: null },
//   shortlisted: { Icon: ShortlistedIcon, color: '#16A34A', tooltip: null },
//   rejected: { Icon: RejectedIcon, color: '#DC2626', tooltip: null },
//   failed: { Icon: FailedIcon, color: '#D97706', tooltip: null },
//   not_pitch_deck: { Icon: FailedIcon, color: '#D97706', tooltip: 'Not a pitch deck' },
//   duplicate_skipped: { Icon: DuplicateIcon, color: '#381dd4', tooltip: 'Duplicate' },
// };

const FILE_STATUS_META = {
  created: { Icon: ShortlistedIcon, color: '#16A34A', tooltip: null },
  shortlisted: { Icon: ShortlistedIcon, color: '#16A34A', tooltip: null },
  rejected: { Icon: RejectedIcon, color: '#DC2626', tooltip: null },
  failed: { Icon: RejectedIcon, color: '#DC2626', tooltip: null },
  not_pitch_deck: { Icon: RejectedIcon, color: '#DC2626', tooltip: null },
  duplicate_skipped: { Icon: RejectedIcon, color: '#DC2626', tooltip: null },
};

const BatchSnackbar = ({ batch, onClose, onToggleCollapsed, onComplete }) => {
  const theme = useTheme();
  const c = theme.palette.customColors;
  const autoCloseTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const hasCalledOnCompleteRef = useRef(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const { removeBatch, updateBatch, markUploadStepCompleted, markBatchNotified } = useProcessingSnackbar();

  const isBulkAnalysis = batch.batchType === 'bulk_analysis';
  const isDealflowShortlist = batch.batchType === 'dealflow_shortlist';
  // No batch id exists for auto-shortlist, so nothing is polled: the view marks it
  // complete via useAutoShortlistRefresh.
  const isAutoShortlist = batch.batchType === 'auto_shortlist';
  const batchConfig = getBatchConfig(batch.batchType);

  // Fetch from appropriate endpoint based on batch type
  // FetchBatchStatus hardcodes `enabled: !!batch_id` and ignores the option passed
  // here, so withholding the id is the only way to stop it firing. auto_shortlist ids
  // are synthetic - requesting one 404s, and the 404 effect below would drop the batch.
  const { data: bulkAnalysisBatchStatus, isError, error } = FetchBatchStatus(isAutoShortlist ? null : batch.batchId, {
    enabled: isBulkAnalysis && !!batch.batchId,
    refetchInterval: batch.uploadStep === 'processing' ? 3000 : false,
  });

  // const { data: dealflowShortlistBatchStatus } = FetchDealflowShortlistBatchStatus({
  //   batch_id: batch.batchId,
  //   enabled: isDealflowShortlist && !!batch.batchId,
  // });

  const batchStatus = bulkAnalysisBatchStatus;

  console.log(batchStatus, "batchStatus");

  // Remove batch from local state if 404 (not found)
  useEffect(() => {
    if (isError && error?.response?.status === 404) {
      removeBatch(batch.batchId);
    }
  }, [isError, error, batch.batchId, removeBatch]);

  useEffect(() => {
    if (batchStatus && ['completed', 'failed', 'partially_completed'].includes(batchStatus.status)) {
      // Mark uploadStep as complete when batch finishes
      if (batch.uploadStep !== 'complete') {
        markUploadStepCompleted(batch.batchId);
      }
      // Mark batch as complete (without removing it immediately)
      // The UI will show completion state; user can close it manually
      if (batch.notifyOnComplete && !batch.notified && Notification.permission === 'granted') {
        markBatchNotified(batch.batchId);
        const total = batchStatus.total_files ?? 0;
        const title = isDealflowShortlist ? 'AI Shortlist Complete' : 'Pitch Deck Analysis Complete';
        new Notification(title, {
          body: `${total} startup${total !== 1 ? 's' : ''} processed.`,
          icon: '/favicon.ico',
        });
      }
      // Trigger onComplete callback for parent components to refetch data (only once)
      if (onComplete && !hasCalledOnCompleteRef.current) {
        hasCalledOnCompleteRef.current = true;
        onComplete(batch.batchType);
      }
    }
  }, [batchStatus?.status, batch.notifyOnComplete, isDealflowShortlist, batch, onComplete, markUploadStepCompleted, markBatchNotified, batch.batchId, batch.uploadStep, batch.notified]);

  // Completion for auto_shortlist rides on uploadStep, not batchStatus.
  useEffect(() => {
    if (!isAutoShortlist || batch.uploadStep !== 'complete') return;
    if (batch.notifyOnComplete && !batch.notified && Notification.permission === 'granted') {
      markBatchNotified(batch.batchId);
      new Notification(batchConfig.notificationTitle, {
        body: batchConfig.notificationBody(0),
        icon: '/favicon.ico',
      });
    }
    if (onComplete && !hasCalledOnCompleteRef.current) {
      hasCalledOnCompleteRef.current = true;
      onComplete(batch.batchType);
    }
  }, [isAutoShortlist, batch.uploadStep, batch.notifyOnComplete, batch.notified, batch.batchId, batch.batchType, onComplete, markBatchNotified, batchConfig]);

  useEffect(() => {
    const isComplete = batch.uploadStep === 'complete' || (batchStatus && ['completed', 'failed', 'partially_completed'].includes(batchStatus.status));

    if (isComplete && batchConfig.closeOnCompletion && batchConfig.autoCloseDelayMs) {
      const delayMs = batchConfig.autoCloseDelayMs;
      setSecondsRemaining(Math.ceil(delayMs / 1000));

      countdownIntervalRef.current = setInterval(() => {
        setSecondsRemaining((prev) => {
          if (prev <= 1) {
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      autoCloseTimerRef.current = setTimeout(() => {
        onClose();
      }, delayMs);

      return () => {
        if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      };
    } else {
      setSecondsRemaining(0);
    }

    return () => {
      if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [batchStatus?.status, batchConfig.closeOnCompletion, batchConfig.autoCloseDelayMs, batch.uploadStep, onClose]);

  const files = batchStatus?.files ?? batch.uploadedFileNames;
  const isComplete = batch.uploadStep === 'complete' || (batchStatus && ['completed', 'failed', 'partially_completed'].includes(batchStatus.status));
  const total = batchStatus?.total_files ?? batchStatus?.total_startups ?? batch?.uploadedFileNames?.length;

  const getItemLabel = () => {
    switch (batch.batchType) {
      case 'bulk_analysis':
        return 'File';
      case 'dealflow_shortlist':
        return 'Startup';
      default:
        return 'Item';
    }
  };

  const itemLabel = getItemLabel();
  const pluralLabel = total !== 1 ? `${itemLabel}s` : itemLabel;

  const tooltipProps = {
    placement: 'top-start',
    arrow: true,
    componentsProps: {
      tooltip: { sx: { backgroundColor: c.g300, color: c.whiteBG, fontSize: '0.75rem', borderRadius: '6px', px: 1.25, py: 0.75, maxWidth: 260 } },
      arrow: { sx: { color: c.g300 } },
    },
  };

  const renderFileRow = (file, i, arr) => {
    let statusIcon;

    if (isBulkAnalysis) {
      const meta = FILE_STATUS_META[file.status];
      statusIcon = meta ? (<DoneIcon sx={{ fontSize: 18, color: '#16A34A', flexShrink: 0, ml: 1 }} />) : (
        <CircularProgress size={18} sx={{ color: c.b300, flexShrink: 0, ml: 1 }} />
      );
    } else {
      const meta = FILE_STATUS_META[file.status];
      const tooltipText = file.failure_reason || meta?.tooltip || '';
      statusIcon = meta ? (
        <meta.Icon sx={{ fontSize: 18, color: meta.color, flexShrink: 0, ml: 1 }} />
      ) : (
        <CircularProgress size={18} sx={{ color: c.b300, flexShrink: 0, ml: 1 }} />
      );
    }

    return (
      <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.875, borderBottom: i < arr.length - 1 ? `1px solid ${c.gray200}` : 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <PdfIcon sx={{ fontSize: 16, color: c.r300, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.82rem', color: c.g300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {file.file_name || 'File'}
          </Typography>
        </Box>
        {statusIcon}
      </Box>
    );
  };

  const handleRequestNotify = async () => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') {
      updateBatch(batch.batchId, { notifyOnComplete: true });
    } else if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        updateBatch(batch.batchId, { notifyOnComplete: true });
      }
    }
  };

  return (
    // auto_shortlist has no item count to gate on.
    (!!total || isAutoShortlist) &&
    <Slide in direction="left" mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'relative',
          width: { xs: 'calc(100vw - 2rem)', sm: 340 },
          maxWidth: 340,
          backgroundColor: c.whiteBG,
          borderRadius: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
          overflow: 'hidden',
        }}
      >
        <Box
          onClick={() => onToggleCollapsed(!batch.collapsed)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            pt: 1.5,
            pb: batch.collapsed ? 1.5 : 1,
            cursor: 'pointer',
            userSelect: 'none',
            borderBottom: batch.collapsed ? 'none' : `1px solid ${c.gray200}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {((isDealflowShortlist || isAutoShortlist) && batch.uploadStep !== 'complete') && <CircularProgress size={18} sx={{ color: c.b300 }} />}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: c.g300 }}>
                {/* "Analyzed", not "Shortlisted": an AI Shortlist run assigns each startup
                    either the chosen shortlisted status or the rejected one, so calling the
                    whole batch shortlisted is wrong for every startup it rejected. */}
                {isAutoShortlist
                  ? (isComplete ? 'Auto-Shortlist Complete' : 'Auto-Shortlisting Startups...')
                  : (isComplete ? `${total} ${pluralLabel} Analyzed` : `Analyzing ${total} ${pluralLabel}...`)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              size="medium"
              sx={{ p: 0.25, color: c.g100 }}
              onClick={(e) => {
                e.stopPropagation();
                onToggleCollapsed(!batch.collapsed);
              }}
            >
              {batch.collapsed ? <ExpandLessIcon sx={{ fontSize: 26 }} /> : <ExpandMoreIcon sx={{ fontSize: 26 }} />}
            </IconButton>
            <Grow in={isComplete} unmountOnExit>
              <IconButton
                size="medium"
                sx={{ p: 0.25, color: c.g100 }}
                onClick={(e) => {
                  e.stopPropagation();
                  localStorage.removeItem('bulk_analysis_state');
                  onClose();
                }}
              >
                <CloseIcon sx={{ fontSize: 24 }} />
              </IconButton>
            </Grow>
          </Box>
        </Box>

        <Collapse in={!batch.collapsed}>
          <Box sx={{ px: 2, py: 1, maxHeight: 220, overflowY: 'auto' }}>
            {isAutoShortlist ? (
              <Typography sx={{ fontSize: '0.82rem', color: c.g100, py: 0.5 }}>
                {isComplete
                  ? 'New startups were shortlisted against your Investment Thesis.'
                  : 'New startups are being shortlisted against your Investment Thesis.'}
              </Typography>
            ) : (
              files.map((file, i, arr) => renderFileRow(file, i, arr))
            )}
          </Box>
          {/* enableNotifications: false drops the whole footer - there is nothing else
              in it, so an empty bordered strip would be left behind. */}
          {batchConfig.enableNotifications !== false && (
          <Box sx={{ borderTop: `1px solid ${c.gray200}`, px: 2, py: 1 }}>
            {isComplete ? secondsRemaining > 0 && (
              <Typography sx={{ fontSize: '0.75rem', color: c.g100 }}>
                Closing in {secondsRemaining}s
              </Typography>
            ) : (
              batch.notifyOnComplete ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <NotifyIcon sx={{ fontSize: 15, color: c.g100 }} />
                  <Typography sx={{ fontSize: '0.82rem', color: c.g100, fontWeight: 500 }}>You'll be notified when complete</Typography>
                </Box>
              ) : Notification.permission !== 'denied' ? (
                <Button
                  fullWidth
                  variant="text"
                  size="small"
                  startIcon={<NotifyIcon sx={{ fontSize: 15 }} />}
                  onClick={handleRequestNotify}
                  sx={{ borderRadius: '8px', fontWeight: 500, fontSize: '0.82rem', textTransform: 'none', color: c.g200 }}
                >
                  Notify me when done
                </Button>
              ) : (
                <Typography sx={{ fontSize: '0.78rem', color: c.g100, textAlign: 'center' }}>Processing in background...</Typography>
              )
            )}
          </Box>
          )}
        </Collapse>
      </Box>
    </Slide>
  );
};

const completionCallbacks = {};

export const registerBatchCompletionCallback = (batchType, callback) => {
  completionCallbacks[batchType] = callback;
};

export const unregisterBatchCompletionCallback = (batchType) => {
  delete completionCallbacks[batchType];
};

const ProcessingSnackbar = () => {
  const { batches, removeBatch } = useProcessingSnackbar();
  const [collapsedStates, setCollapsedStates] = React.useState(new Map());

  const handleBatchComplete = React.useCallback((batchType) => {
    if (completionCallbacks[batchType]) {
      completionCallbacks[batchType]();
    }
  }, []);

  if (batches.size === 0) return null;

  const batchArray = Array.from(batches.values());

  console.log('Rendering ProcessingSnackbar with batches:', batchArray);

  const handleToggleCollapsed = (batchId, collapsed) => {
    setCollapsedStates((prev) => {
      const next = new Map(prev);
      next.set(batchId, collapsed);
      return next;
    });
  };

  return (
    <Box sx={{ position: 'fixed', bottom: 0, right: 0, zIndex: 1400, display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row-reverse' }, gap: 1, p: { xs: 1, sm: 2 }, alignItems: "flex-end" }}>
      {batchArray.map((batch, index) => (
        <BatchSnackbar
          key={batch.batchId}
          batch={{ ...batch, collapsed: collapsedStates.get(batch.batchId) ?? batch.collapsed }}
          onClose={() => removeBatch(batch.batchId)}
          onToggleCollapsed={(collapsed) => handleToggleCollapsed(batch.batchId, collapsed)}
          onComplete={handleBatchComplete}
        />
      ))}
    </Box>
  );
};

export default ProcessingSnackbar;
